import React, { useEffect, useRef } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { Button, Grid, withTheme, Divider, LinearProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import moment from '../moment-hawaii';

import BookingProgressBar from './fancy/BookingProgressBar';
import { PAYMENT_METHODS } from '../hooks/usePaymentMethod';
import BookingTable from './profile/BookingTable';
import { useGA } from '../hooks/useGoogleAnalytics';
import postActiviterMessage from '../utils/postActiviterMessage';

const BookingInProgress = () => (
    <Grid container spacing={4} className="page" justifyContent="center" alignItems="center">
        <Grid item xs={12} sm={6} lg={4} container spacing={2}>
            <Grid item xs={12}>
                <h1>Booking in progress...</h1>
            </Grid>
            <Grid item xs={12}>
                <LinearProgress />
            </Grid>
            <Grid item xs={12}>
                <p>Currently we are reaching out to providers and verify each booking.</p>
                <p>This takes time, please wait until the page loads the result.</p>
            </Grid>
        </Grid>
    </Grid>
);

const BookingError = ({ error }) => (
    <Grid container spacing={4} className="page" justifyContent="center" alignItems="center">
        <Grid item xs={12} sm={6} lg={4} container spacing={2}>
            <Grid item xs={12}>
                <h1>Booking failed</h1>
            </Grid>
            <Grid item xs={12}>
                <p>We have encountered some issues during the booking.</p>
            </Grid>
            <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
            </Grid>
            <Grid item xs={12}>
                <Link to="/cart">
                    <Button variant="contained" fullWidth>
                        Back to the cart
                    </Button>
                </Link>
            </Grid>
        </Grid>
    </Grid>
);

const ConfirmationPage = ({ createResult, customer, theme }) => {
    const { track } = useGA();
    const purchaseTracked = useRef(false);

    // Notify parent window (iframe + Flutter WebView) that booking is complete
    useEffect(() => {
        if (createResult && typeof createResult !== 'string') {
            const timer = setTimeout(() => {
                postActiviterMessage({
                    type: 'BOOKING_COMPLETE',
                    bookingId: createResult.bookingId
                });
            }, 10000);
            return () => clearTimeout(timer);
        }
    }, [createResult]);

    // Track purchase event when booking is successfully confirmed
    useEffect(() => {
        if (createResult && typeof createResult !== 'string' && !purchaseTracked.current) {
            // Extract items from booking result
            const items = [];
            createResult.cartItems.forEach(cartItems => {
                cartItems?.tickets.forEach(ticket => {
                    items.push({
                        item_id: `${cartItems.activity?.id}-${ticket.id}`,
                        item_name: `${cartItems.activity?.name} | ${ticket.name}`,
                        price: ticket.ticket_retail_price || ticket.retail_price || 0,
                        quantity: ticket.quantity || 1
                    });
                });
            });

            const value = createResult.bookingItems.reduce((total, item) => {
                return total + (item.total_price || 0);
            }, 0);

            track.purchase({
                transaction_id: createResult.bookingId?.toString() || Date.now().toString(),
                value,
                items,
                tax: 0,
                currency: 'USD',
                customer_type: customer?.role || 'guest'
            });

            purchaseTracked.current = true;
        }
    }, [createResult, customer, track]);

    // basicly the loading screen
    if (createResult === null) {
        if (!customer) {
            return <Redirect to="/" />;
        }
        return <BookingInProgress />;
    }

    // Error screen
    if (typeof createResult === 'string') {
        return <BookingError error={createResult} />;
    }

    // the agent can drop the guest session while this page is open, so customer
    // may be null here; the booking response carries the booker and card data
    const nameSource = customer?.firstname || customer?.lastname ? customer : createResult.booker;
    const bookingName = [nameSource?.firstname, nameSource?.lastname].filter(Boolean).join(' ');
    const cardBrand = customer?.stripe_brand || createResult.brand;
    const cardLast4 = customer?.stripe_last4 || createResult.last4;

    return (
        <Grid container className="page" spacing={2}>
            <Grid item container md={8} xs={12}>
                <Link
                    to="/payment"
                    className="link mb-2"
                    style={{ color: theme.palette.primary.main }}
                >
                    Back to Payment
                </Link>
                <Grid item xs={12}>
                    <h1 className="pageheader">Booking Result</h1>
                </Grid>
                {createResult.bookingItems.find(e => e.status === 'confirmed') === undefined && (
                    <Grid item xs={12}>
                        <p>
                            Mahalo! We did the booking, the results can be seen here. You will
                            receive emails on the successful bookings!
                        </p>
                    </Grid>
                )}
                {createResult.bookingItems.find(e => e.status === 'unconfirmed') !== undefined && (
                    <Grid item xs={12}>
                        <p>
                            Mahalo! Some items need to be confirmed by the vendor. We sent an email
                            to the vendor, the booking information can be seen here. You will
                            receive emails on any changes in the booking status!
                        </p>
                    </Grid>
                )}
                <Grid item xs={12}>
                    <p>
                        We will notify you on any event using:{' '}
                        {createResult.notification.map(field => (
                            <span key={field}>{field} </span>
                        ))}
                    </p>
                </Grid>
            </Grid>
            <Grid item className="mb-2" md={4} xs={12}>
                <BookingProgressBar activeStep={3} />
            </Grid>

            <Grid item container spacing={2}>
                <Grid item xs={12} md={8}>
                    <BookingTable bookingItems={createResult.bookingItems} onGuestPage={false} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <div style={{ padding: '30px', backgroundColor: '#fafafa' }}>
                        <h2>Payment Details</h2>
                        <Divider className="mb-3" style={{ backgroundColor: '#4a4a4a' }} />
                        <p>
                            <b>Booking Name:</b> {bookingName}
                        </p>
                        <p>
                            <b>Payment Date:</b> {moment().format('MMM DD, YYYY')}
                        </p>
                        <p>
                            <b>Payment Method:</b>{' '}
                            {createResult?.payment_method === PAYMENT_METHODS.card
                                ? cardBrand && cardLast4 && `${cardBrand} *${cardLast4}`
                                : 'Charge to House Account'}
                        </p>
                    </div>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default withTheme(ConfirmationPage);
